import { useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  importOrdersExcel,
  type OrderExcelImportResult,
} from "@/lib/orders";

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Import fehlgeschlagen.";
}

function ResultSummary({ result }: { result: OrderExcelImportResult }) {
  const importedLabel = result.dry_run ? "Würden angelegt" : "Angelegt";
  return (
    <Alert variant={result.failed > 0 ? "warning" : "success"} title={result.dry_run ? "Prüfung" : "Import"}>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-label-secondary">Zeilen</dt>
          <dd className="font-medium">{result.rows}</dd>
        </div>
        <div>
          <dt className="text-label-secondary">{importedLabel}</dt>
          <dd className="font-medium">{result.imported}</dd>
        </div>
        <div>
          <dt className="text-label-secondary">Schon vorhanden</dt>
          <dd className="font-medium">{result.skipped_existing}</dd>
        </div>
        <div>
          <dt className="text-label-secondary">Anderer Status</dt>
          <dd className="font-medium">{result.skipped_status}</dd>
        </div>
        <div>
          <dt className="text-label-secondary">Nicht übernommen</dt>
          <dd className="font-medium">{result.failed}</dd>
        </div>
        <div>
          <dt className="text-label-secondary">Lieferant erkannt</dt>
          <dd className="font-medium">{result.linked_suppliers}</dd>
        </div>
      </dl>
      {result.counter.length > 0 && (
        <p className="mt-3 text-sm">
          Jahreszähler mindestens{" "}
          {result.counter
            .map((entry) => `${entry.jahr}: ${entry.last_lauf_at_least}`)
            .join(", ")}
          {result.dry_run ? " (noch nicht gespeichert)" : ""}.
        </p>
      )}
      {result.errors.length > 0 && (
        <ul className="mt-3 max-h-64 list-disc space-y-1 overflow-y-auto pl-5 text-sm">
          {result.errors.map((entry) => (
            <li key={`${entry.row}-${entry.bestellnummer ?? ""}-${entry.reason}`}>
              Zeile {entry.row}
              {entry.bestellnummer ? ` (${entry.bestellnummer})` : ""}: {entry.reason}
            </li>
          ))}
        </ul>
      )}
      {result.errors_truncated && (
        <p className="mt-2 text-sm">Weitere Fehler sind in der Antwort nicht enthalten.</p>
      )}
    </Alert>
  );
}

export function BestellungenSettingsPage() {
  const [file, setFile] = useState<File | null>(null);
  const [includeAngefragt, setIncludeAngefragt] = useState(false);
  const [running, setRunning] = useState<"check" | "import" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OrderExcelImportResult | null>(null);

  async function run(dryRun: boolean) {
    if (!file || running) return;
    if (!dryRun) {
      const ok = window.confirm(
        "Die Bestellungen aus der Datei werden angelegt. Schon vorhandene Bestellnummern bleiben unverändert.",
      );
      if (!ok) return;
    }
    setRunning(dryRun ? "check" : "import");
    setError(null);
    try {
      const next = await importOrdersExcel(file, { dryRun, includeAngefragt });
      setResult(next);
    } catch (err) {
      setResult(null);
      setError(errorMessage(err));
    } finally {
      setRunning(null);
    }
  }

  return (
    <Card
      title="Excel-Bestellungen"
      description="Bestellungen aus dem Blatt „Bestellungen“ einer Excel-Liste übernehmen. Zeilen mit Status Bestellt werden angelegt. Schon vorhandene Bestellnummern werden übersprungen."
    >
      <div className="space-y-4">
        <Alert variant="info">
          Zeilen ohne Projektnummer (000) werden nur übernommen, wenn im Ordner ein Auftrag mit
          der Nummer 0 existiert. Mit „Prüfen“ siehst du das Ergebnis, ohne etwas zu speichern.
        </Alert>

        <label className="block text-sm font-medium text-label">
          Datei
          <input
            type="file"
            accept=".xlsx,.xlsm"
            className="eh-input mt-1"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setResult(null);
              setError(null);
            }}
          />
        </label>

        <Checkbox
          label="Anfragen mit importieren"
          description="Zeilen mit Status Angefragt ebenfalls als Bestellung anlegen."
          checked={includeAngefragt}
          onChange={(event) => {
            setIncludeAngefragt(event.target.checked);
            setResult(null);
          }}
        />

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={!file || running !== null}
            loading={running === "check"}
            onClick={() => void run(true)}
          >
            Prüfen
          </Button>
          <Button
            type="button"
            disabled={!file || running !== null}
            loading={running === "import"}
            onClick={() => void run(false)}
          >
            Importieren
          </Button>
        </div>

        {error && (
          <Alert variant="error" title="Fehler">
            {error}
          </Alert>
        )}
        {result && <ResultSummary result={result} />}
      </div>
    </Card>
  );
}
